import { useMemo, useCallback } from 'react';
import cn from 'classnames';

import { BaseCell } from '../BaseCell';
import { Column } from '../types';

import styles from './TableHead.module.scss';

type Props<T> = {
  columns: Column<T>[];
  isMobile: boolean;
};

export function TableHead<T>({ columns, isMobile }: Props<T>) {
  const autoColumnWidth = useMemo(() => {
    const columnsWithSize = columns.filter((c) => c.size);
    const totalColumnSize = columnsWithSize.reduce(
      (acc, c) => acc + (c.size as number),
      0
    );
    const emptySpace = 10 - totalColumnSize;
    return emptySpace / (columns.length - columnsWithSize.length);
  }, [columns]);

  const getSize = useCallback(
    (c: Column<T>) => {
      if (!isMobile) return undefined;
      if (c.width)
        return typeof c.width === 'number' ? `${c.width}px` : c.width;
      return `${(c.size ?? autoColumnWidth * (c.thProps?.colSpan ?? 1)) * 10}%`;
    },
    [isMobile, autoColumnWidth]
  );

  return (
    <thead>
      <tr>
        {columns.slice().map((c, idx, arr) => {
          const { colSpan } = c.thProps || {};
          if (colSpan) {
            arr.splice(idx + 1, colSpan - 1);
          }
          return (
            <BaseCell
              as="th"
              {...c.thProps}
              key={c.name}
              className={cn(
                styles.tableHead,
                styles[`position-${c.position || 'left'}`],
                c.thProps?.className
              )}
              style={{
                width: getSize(c),
                ...c.thProps?.style,
              }}
            >
              {c.label}
            </BaseCell>
          );
        })}
      </tr>
    </thead>
  );
}
